import { Lock } from "Shared/appIcons";
import type { ComponentType, PropsWithChildren } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type IconComponent = ComponentType<{
	className?: string;
	strokeWidth?: number;
}>;

export type NavItem = {
	key: string;
	label: string;
	icon: IconComponent;
	to?: string;
	group?: "top" | "bottom";
	disabled?: boolean;
	hint?: string;
	// Optional chunk preloader fired on hover/focus so the route's JS is
	// already in cache by the time the user clicks. See `AppRouter.tsx`'s
	// `preload*` factories.
	preload?: () => Promise<unknown>;
};

type Props = {
	item: NavItem;
	active: boolean;
	collapsed: boolean;
};

export const NavGroup = ({ children }: PropsWithChildren) => (
	<div className="flex flex-col gap-0.5">{children}</div>
);

export const NavDivider = () => <div className="bg-border mx-2 my-3 h-px" />;

export const NavButton = ({ item, active, collapsed }: Props) => {
	const Icon = item.icon;
	const disabled = !!item.disabled;
	const showLockBadge = disabled && !!item.hint;

	if (collapsed) {
		const className = cn(
			"relative w-10 h-10 mx-auto flex items-center justify-center rounded-md transition-colors",
			disabled
				? "text-muted-foreground cursor-not-allowed"
				: active
					? "bg-primary-light text-primary"
					: "text-muted-foreground hover:bg-muted hover:text-foreground",
		);

		const iconClass = cn(
			"w-[18px] h-[18px]",
			disabled ? "text-muted-foreground" : active ? "text-primary" : "",
		);

		const inner = (
			<>
				<Icon className={iconClass} strokeWidth={1.75} />
				{disabled ? (
					<Lock className="text-muted-foreground absolute right-1 bottom-1 h-2 w-2" />
				) : null}
			</>
		);

		if (disabled || !item.to) {
			return (
				<Button
					variant="ghost"
					disabled
					title={showLockBadge ? `${item.label} · ${item.hint}` : item.label}
					className={className}
				>
					{inner}
				</Button>
			);
		}

		return (
			<Link
				to={item.to}
				title={item.label}
				className={className}
				onMouseEnter={item.preload}
				onFocus={item.preload}
			>
				{inner}
			</Link>
		);
	}

	const className = cn(
		"w-full flex items-center gap-2.5 px-3 h-9 rounded-md text-small text-left transition-colors no-underline",
		disabled
			? "text-muted-foreground cursor-not-allowed"
			: active
				? "bg-primary-light text-primary font-semibold"
				: "text-foreground hover:bg-muted",
	);

	const iconClass = cn(
		"w-4 h-4 flex-shrink-0",
		disabled
			? "text-muted-foreground"
			: active
				? "text-primary"
				: "text-muted-foreground",
	);

	const inner = (
		<>
			<Icon className={iconClass} strokeWidth={1.75} />
			<span className="flex-1 truncate">{item.label}</span>
			{showLockBadge ? (
				<span className="text-tiny text-muted-foreground bg-muted flex items-center gap-1 rounded px-1.5 py-0.5 uppercase">
					<Lock className="h-2.5 w-2.5" /> {item.hint}
				</span>
			) : null}
		</>
	);

	if (disabled || !item.to) {
		return (
			<Button variant="ghost" disabled className={className}>
				{inner}
			</Button>
		);
	}

	return (
		<Link
			to={item.to}
			className={className}
			onMouseEnter={item.preload}
			onFocus={item.preload}
		>
			{inner}
		</Link>
	);
};
